import { IgAutocomplete } from "@intelligems/ig-theme/IgAutocomplete";
import { IgDot } from "@intelligems/ig-theme/IgDot";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { MenuItem, Stack, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "@src/store";
import { ExperienceBuilderAiSelector } from "@src/store/slices/experienceBuilder/ai";
import {
  ExperienceBuilderAction,
  ExperienceBuilderSelector,
} from "@src/store/slices/experienceBuilder";

export const ThemeSelector = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const targetThemeId = useSelector(ExperienceBuilderSelector.getTargetThemeId);
  const themes = useSelector(ExperienceBuilderSelector.getThemes);
  const isAuthenticated = useSelector(
    ExperienceBuilderSelector.isAuthenticated
  );
  const isAiBusy = useSelector(ExperienceBuilderAiSelector.getIsAiBusy);

  const hasThemes = themes.length > 0;

  const selectedTheme =
    (targetThemeId !== undefined
      ? themes.find((t) => t.id === targetThemeId)
      : null) ??
    themes[0] ??
    null;

  // Three states, not two. An empty `themes` means either of two different
  // things: a demo URL is set, so there is no store we can list themes for; or
  // this is the org's own store and the list came back empty — including when
  // the `/themes` call failed, which `ShopifyThemesManager` and `ShopifyClient`
  // both swallow into `[]`. Deriving the demo copy from emptiness alone told
  // authenticated merchants their themes were unavailable "for demo URL's"
  // with no demo URL anywhere in play.
  const tooltipTitle = isAiBusy
    ? "AI is busy, please wait..."
    : !isAuthenticated
      ? "Themes are not available for demo URL's"
      : !hasThemes
        ? "No themes found for this store"
        : "";

  return (
    <IgTooltip
      title={tooltipTitle}
      wrapperStyle={{
        display: "flex",
        flex: 1,
        minWidth: 120,
        maxWidth: 240,
      }}
    >
      <IgAutocomplete
        size="small"
        disabled={isAiBusy || !hasThemes}
        disableClearable
        options={themes}
        value={selectedTheme}
        onChange={(_e, value) => {
          dispatch(ExperienceBuilderAction.setTargetThemeId(value?.id));
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            height: 32,
            fontSize: 12,
            borderRadius: "6px",
          },
        }}
        slotProps={{
          popper: {
            placement: "bottom-start",
            modifiers: [{ name: "flip", enabled: false }],
            sx: {
              width: "auto !important",
              maxWidth: 350,
            },
          },
        }}
        renderInput={(params) => (
          <IgTextField
            {...params}
            placeholder={hasThemes ? undefined : "No themes"}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <MenuItem
              key={String(key)}
              {...optionProps}
              selected={option.id === selectedTheme?.id}
            >
              <Stack direction="row" alignItems="center" spacing={1} useFlexGap>
                {option.role === "main" && (
                  <IgDot color={theme.igIconColors.success} />
                )}
                {option.name}
              </Stack>
            </MenuItem>
          );
        }}
      />
    </IgTooltip>
  );
};
